import { useSearchParams } from 'react-router-dom'
import { compact } from 'lodash'
import moment from 'moment'
import { useEffect, useMemo, useRef } from 'react'
import { useActivityDrawer } from 'app/hooks/useActivityDrawer'
import { useActivityReload } from 'app/hooks/useActivityReload'
import { formatPercent } from 'app/lib/utils'
import { useVoiceAgentOpsStore } from 'app/stores/StoreContext'
import {
  callFailureColumns,
  toolFailureCallColumns,
  unbookedCallColumns,
  voiceAgentOpsDippingColumns,
  voiceAgentOpsLocationColumns,
} from 'app/table-columns/voice-agent-ops'
import { useOrganization, useWorkspace } from 'app/utils'
import { StatsCardType } from 'types/StatsCardType'
import { useOpsCallDetail } from './useOpsCallDetail'
import {
  CALL_FAILURE_COUNT_KEY,
  CALL_FAILURE_GROUP,
  CALL_FAILURE_GROUP_LABEL,
  CALL_FAILURE_LABEL,
  CALL_FAILURE_TYPES,
  METRIC_DEFINITIONS,
  TOOL_FLAG_COUNT_KEY,
  TOOL_FLAG_METRIC_LABEL,
  TOOL_FLAGS,
} from './voiceAgentOpsMetrics'
import type { CallFailureType, ToolFlag, VoiceAgentOpsLocationRow } from './voiceAgentOps.types'

export type { CallFailureFilter, FlagFilter, SlippingScope } from 'app/stores/VoiceAgentOpsStore'

/**
 * Wires the super-user Ops tab to `VoiceAgentOpsStore`: it pushes the page's scope
 * (`?fromDate` / `?toDate` / `?locationIds`, and whether a workspace is selected) into
 * the store, asks it to load, and returns the store's data with the React-only pieces
 * (table columns, the drawers, the scroll targets).
 *
 * Every value here is read from MobX observables, so `VoiceAgentOpsTab` is an
 * `observer` and re-renders when the store changes.
 */
export function useVoiceAgentOps() {
  const [searchParams, setSearchParams] = useSearchParams()
  const workspace = useWorkspace()
  const organization = useOrganization()
  const store = useVoiceAgentOpsStore()
  const drawer = useActivityDrawer(`voice-agent-ops`)
  const callDetail = useOpsCallDetail()
  const callListRef = useRef<HTMLDivElement>(null)
  const callFailureListRef = useRef<HTMLDivElement>(null)

  const fromDate = searchParams.get(`fromDate`) || moment().startOf(`month`).format(`YYYY-MM-DD`)
  const toDate = searchParams.get(`toDate`) || moment().endOf(`month`).format(`YYYY-MM-DD`)
  const locationIdsParam = searchParams.get(`locationIds`)
  const selectedLocationIds = useMemo(() => compact(locationIdsParam?.split(`,`) ?? []), [locationIdsParam])

  const loadActivity = useActivityReload(drawer.setFocusedActivity, store.setCalls)

  // Declared first so the store knows the scope before either load effect runs.
  useEffect(() => {
    store.setScope(fromDate, toDate, selectedLocationIds, !!workspace, organization?.id)
  }, [store, fromDate, toDate, selectedLocationIds, workspace?.id, organization?.id])

  useEffect(() => {
    store.loadLocations()
  }, [store, workspace?.id])

  useEffect(() => {
    store.loadMetrics()
  }, [store, fromDate, toDate, locationIdsParam, workspace?.id])

  useEffect(() => {
    store.loadDailyMetrics()
  }, [store, fromDate, toDate, locationIdsParam, workspace?.id])

  useEffect(() => {
    store.loadCalls()
  }, [store, fromDate, toDate, locationIdsParam, workspace?.id, organization?.id])

  const summary = store.summary

  const showFailedCalls = (flag: ToolFlag) => {
    store.setFlagFilter(flag)
    callListRef.current?.scrollIntoView({ behavior: `smooth`, block: `start` })
  }

  const showCallFailures = (type: CallFailureType) => {
    store.setFailureFilter(type)
    callFailureListRef.current?.scrollIntoView({ behavior: `smooth`, block: `start` })
  }

  const performanceStats: StatsCardType[] = [
    {
      label: `Schedule Rate`,
      value: `${formatPercent(summary.scheduleRate)}%`,
      infoTooltip: { title: `Schedule Rate`, message: METRIC_DEFINITIONS.scheduleRate },
    },
    {
      label: `AI Calls`,
      value: summary.aiCalls.toLocaleString(`en-US`),
      infoTooltip: { title: `AI Calls`, message: METRIC_DEFINITIONS.aiCalls },
    },
    {
      label: `New Patient Calls`,
      value: summary.newPatientCalls.toLocaleString(`en-US`),
      infoTooltip: { title: `New Patient Calls`, message: METRIC_DEFINITIONS.newPatientCalls },
    },
    {
      label: `Appointments Booked`,
      value: summary.appointmentsBooked.toLocaleString(`en-US`),
      infoTooltip: { title: `Appointments Booked`, message: METRIC_DEFINITIONS.appointmentsBooked },
    },
    {
      label: `Appointments Completed`,
      value: summary.appointmentsCompleted.toLocaleString(`en-US`),
      infoTooltip: { title: `Appointments Completed`, message: METRIC_DEFINITIONS.appointmentsCompleted },
    },
  ]

  const failureStats: StatsCardType[] = [
    ...TOOL_FLAGS.map((flag) => ({
      label: TOOL_FLAG_METRIC_LABEL[flag],
      value: summary[TOOL_FLAG_COUNT_KEY[flag]].toLocaleString(`en-US`),
      infoTooltip: { title: TOOL_FLAG_METRIC_LABEL[flag], message: METRIC_DEFINITIONS[TOOL_FLAG_COUNT_KEY[flag]] },
      onOpenClick: () => showFailedCalls(flag),
    })),
    {
      // One decimal: failure rates live in the low single digits, where whole percents hide the signal.
      label: `Failure Rate`,
      value: summary.failureRate === undefined ? `-` : `${summary.failureRate.toFixed(1)}%`,
      infoTooltip: { title: `Failure Rate`, message: METRIC_DEFINITIONS.failureRate },
    },
  ]

  const callFailureStats: StatsCardType[] = CALL_FAILURE_TYPES.map((type) => ({
    // Type name only: a "Group · Type" label wraps when the card's open icon appears on hover.
    label: CALL_FAILURE_LABEL[type],
    value: summary[CALL_FAILURE_COUNT_KEY[type]].toLocaleString(`en-US`),
    infoTooltip: {
      title: `${CALL_FAILURE_GROUP_LABEL[CALL_FAILURE_GROUP[type]]} · ${CALL_FAILURE_LABEL[type]}`,
      message: METRIC_DEFINITIONS[CALL_FAILURE_COUNT_KEY[type]],
    },
    onOpenClick: () => showCallFailures(type),
  }))

  const focusLocation = (row: VoiceAgentOpsLocationRow) => {
    setSearchParams((prev) => {
      prev.set(`locationIds`, row.locationId)
      prev.set(`integrationStatus`, ``)
      return prev
    })
  }

  const locationColumns = useMemo(
    () => voiceAgentOpsLocationColumns({ showWorkspace: !workspace, onLocationClick: focusLocation }),
    [workspace?.id],
  )

  const dippingColumns = useMemo(
    () => voiceAgentOpsDippingColumns({ showWorkspace: !workspace, onLocationClick: focusLocation }),
    [workspace?.id],
  )

  const failedCallColumns = useMemo(
    () => toolFailureCallColumns({ baseUrl: drawer.baseUrl, onCallClick: callDetail.openCall }),
    [drawer.baseUrl],
  )

  const callFailureListColumns = useMemo(
    () => callFailureColumns({ baseUrl: drawer.baseUrl, onCallClick: callDetail.openCall }),
    [drawer.baseUrl],
  )

  const unbookedListColumns = useMemo(
    () => unbookedCallColumns({ baseUrl: drawer.baseUrl, onCallClick: callDetail.openCall }),
    [drawer.baseUrl],
  )

  const selectedCall = callDetail.selectedCall
  const selectedCallLocationName =
    store.locations?.find((location) => location.id === selectedCall?.locationId)?.name ?? undefined
  const openSelectedCallActivity = selectedCall
    ? () => {
        callDetail.closeCall()
        drawer.selectActivity(selectedCall)
      }
    : undefined

  return {
    fromDate,
    toDate,
    previousFromDate: store.previousFromDate,
    previousToDate: store.previousToDate,
    isLoading: store.isLoading,
    summary,
    performanceStats,
    failureStats,
    daily: store.daily,
    locationRows: store.locationRows,
    locationColumns,
    dippingRows: store.dippingRows,
    dippingColumns,
    unbookedCallCount: store.unbookedCallCount,
    reasonRows: store.reasonRows,
    reasonFilter: store.activeReasonFilter,
    setReasonFilter: store.setReasonFilter,
    visibleUnbookedCalls: store.visibleUnbookedCalls,
    unbookedListColumns,
    isSlippingLoading: store.isSlippingLoading,
    slippingRows: store.slippingRows,
    slippingScope: store.slippingScope,
    setSlippingScope: store.setSlippingScope,
    failedCalls: store.failedCalls,
    failedCallColumns,
    flagFilter: store.flagFilter,
    setFlagFilter: store.setFlagFilter,
    flagCounts: store.flagCounts,
    callListRef,
    callFailureStats,
    visibleCallFailures: store.visibleCallFailures,
    callFailureListColumns,
    failureFilter: store.failureFilter,
    setFailureFilter: store.setFailureFilter,
    failureCounts: store.failureCounts,
    callFailureListRef,
    drawer,
    loadActivity,
    callDetail,
    selectedCallLocationName,
    openSelectedCallActivity,
  }
}

export type UseVoiceAgentOpsReturn = ReturnType<typeof useVoiceAgentOps>
